// D03 Mission 09 — EIGRP Fundamentals (LEARN)
import { readConfirm, predict, observe } from '../../ui/widgets.js';
import { regional, regionalLayout, eigrpDesign } from './topologies.js';
import { ran, norm, rib } from './helpers.js';
import { converge } from '../../engine/net/routing.js';
import { clone } from '../../engine/net/model.js';

function network() {
  const n = eigrpDesign(regional({ backup: true }));
  // The R1–R3 backup is a slower copper path: 1 ms of delay (100 × 10 µs).
  n.devices.R1.interfaces['GigabitEthernet0/2'].delay = 100;
  n.devices.R3.interfaces['GigabitEthernet0/2'].delay = 100;
  return n;
}
const topo = (rt) => converge(clone(rt.net)).eigrp.state['R1|100']?.topology['10.30.2.0/24'];
const succ = (rt) => topo(rt)?.paths.find((p) => p.successor);
const fs = (rt) => topo(rt)?.paths.find((p) => p.feasible);

export default {
  id: 'u09', num: '09', kind: 'LEARN', title: 'EIGRP Fundamentals', type: 'Lesson', implemented: true,
  objective: 'Understand EIGRP neighbours, the composite metric and DUAL — successor, feasible distance, reported distance and feasible successor — on a live topology table.',
  network, layout: regionalLayout({ proto: { a: 'EIGRP 100', b: 'EIGRP 100', bk: 'EIGRP 100 · delay 1 ms' } }), linkLights: true,
  stages: [
    {
      id: 'learn', phase: 'LEARN', title: 'EIGRP and DUAL, gently', workspace: false,
      content: `<p><b>EIGRP</b> (Enhanced Interior Gateway Routing Protocol) is an advanced distance-vector protocol. Like RIP it learns from neighbours; unlike RIP it keeps <i>every</i> neighbour's offer and can switch paths instantly.</p>
<ul><li><b>Neighbours</b>: Hellos every 5 s. Neighbours must share the <b>autonomous-system number</b> (<code>router eigrp 100</code>) and the subnet.</li>
<li><b>Metric</b>: by default bandwidth (the slowest link on the path) and delay (the sum of delays). Higher bandwidth and lower delay = better.</li>
<li><b>Reported distance (RD)</b>: the neighbour's own metric to the network — what it tells you.</li>
<li><b>Feasible distance (FD)</b>: your best total metric to the network.</li>
<li><b>Successor</b>: the neighbour on the best path — installed in the routing table (code <code>D</code>, AD 90).</li>
<li><b>Feasible successor</b>: a backup neighbour whose <b>RD is lower than your FD</b>. That rule guarantees the backup does not loop back through you, so EIGRP can switch to it immediately — no recalculation (DUAL's "feasibility condition").</li></ul>
<pre>P 10.30.2.0/24, 1 successors, FD is 3328
        via 10.30.12.1 (3328/3072), GigabitEthernet0/0      ← successor   (metric/RD)
        via 10.30.13.2 (28416/2816), GigabitEthernet0/2     ← feasible successor (2816 &lt; 3328)</pre>`,
      widgets: [readConfirm({ id: 'd3u09-read' })],
    },
    {
      id: 'observe', phase: 'UNDERSTAND', title: 'Read the topology table on R1', devices: ['R1', 'R2', 'R3'],
      content: '<p>EIGRP AS 100 runs on all routers. The R1–R3 copper backup has a higher delay than the fibre path via R2.</p>',
      evidence: [
        { id: 'n', text: 'R1: show ip eigrp neighbors', match: ran('R1', 'show ip eigrp neighbors') },
        { id: 't', text: 'R1: show ip eigrp topology', match: ran('R1', 'show ip eigrp topology') },
        { id: 'r', text: 'R1: routing table displayed', match: (ev) => ev.device === 'R1' && /^show ip route/.test(ev.key) },
      ],
      widgets: [observe({
        id: 'd3u09-obs', title: 'DUAL ON 10.30.2.0/24 (BRANCH B)',
        fields: [
          { label: 'Successor next hop', check: (v, rt) => norm(v) === succ(rt)?.nh || 'The first "via" line for 10.30.2.0/24.' },
          { label: 'Feasible distance (FD)', check: (v, rt) => norm(v) === String(topo(rt)?.fd) || '"FD is …" on the prefix line.' },
          { label: 'Feasible successor next hop', check: (v, rt) => norm(v) === fs(rt)?.nh || 'The other "via" line — its RD is below the FD.' },
          { label: 'Reported distance of the feasible successor', check: (v, rt) => norm(v) === String(fs(rt)?.rd) || 'The second number in (metric/RD).' },
        ],
      })],
    },
    {
      id: 'failover', phase: 'VERIFY', title: 'Lose the successor', devices: ['R1', 'R2'],
      widgets: [predict({ id: 'd3u09-pred', prompt: 'You shut R1 G0/0 (the link to the successor). What does R1 do for 10.30.2.0/24?', options: ['Installs the feasible successor immediately', 'Queries all neighbours and waits', 'Loses the route until the link returns'] })],
      content: '<p>Shut R1 G0/0, then look at the routing table and topology table again.</p>',
      evidence: [
        { id: 'r', text: 'R1: route to 10.30.2.0/24 now via 10.30.13.2', match: (ev) => ev.device === 'R1' && /^show ip route/.test(ev.key) && /D\s+10\.30\.2\.0\/24 \[90\/28416\] via 10\.30\.13\.2/.test(ev.output) },
      ],
      tasks: [{ id: 'fs', text: 'R1 uses the former feasible successor', check: (rt) => rib(rt, 'R1').some((r) => r.net === '10.30.2.0' && r.prefix === 24 && r.nh === '10.30.13.2'), fail: 'R1 is not using the backup path yet — is G0/0 shut down?' }],
      hints: ['<code>interface g0/0</code> → <code>shutdown</code> on R1, then <code>do show ip route eigrp</code>.'],
    },
  ],
  review: () => '<p>Because the backup met the feasibility condition, EIGRP switched without asking anyone. Bring R1 G0/0 back up if you keep experimenting — this lab is yours to reset.</p>',
};
